import React, { useState, useRef, useEffect } from "react";
import {
  Search, Bell, LayoutGrid, ChevronDown, Home,
  Users, Car, Route, MessageSquare, Coins, BarChart3, Target, Radio, Terminal,
  LogOut, User as UserIcon, Settings, LayoutDashboard,
} from "lucide-react";
import { ASSETS } from "../config/brand";
import NotificationsDropdown from "./NotificationsDropdown";
import MyAccountModal from "./MyAccountModal";
import ThemeToggle from "./ThemeToggle";

const MODULES = [
  { id: "kpis",      label: "Panel Ejecutivo",  icon: LayoutDashboard, accent: "yellow", badge: "NUEVO" },
  { id: "live",      label: "Central en vivo",  icon: Radio,         accent: "danger", badge: "LIVE" },
  { id: "users",     label: "Usuarios",         icon: Users,         accent: "indigo" },
  { id: "fleet",     label: "Flota",            icon: Car,           accent: "indigo" },
  { id: "rides",     label: "Carreras",         icon: Route,         accent: "neon" },
  { id: "tariffs",   label: "Tarifas y zonas",  icon: Coins,         accent: "yellow" },
  { id: "bot",       label: "Bot WhatsApp",     icon: MessageSquare, accent: "success" },
  { id: "botlogs",   label: "Bot Logs · 2chat", icon: Terminal,      accent: "indigo" },
  { id: "reports",   label: "Reportes",         icon: BarChart3,     accent: "neon" },
  { id: "sos",       label: "Alertas SOS",      icon: Target,        accent: "danger", badge: "!" },
];

export function getModuleLabel(id) {
  return MODULES.find(m => m.id === id)?.label || id;
}

export default function AppShell({ active, onNavigate, children, user, onLogout }) {
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [showMyAccount, setShowMyAccount] = useState(false);
  const switcherRef = useRef(null);
  const userMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (switcherRef.current && !switcherRef.current.contains(e.target)) setSwitcherOpen(false);
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isLauncher = active === "launcher";
  const currentModule = MODULES.find(m => m.id === active);

  return (
    <div className="min-h-screen text-mdd-text relative">
      <div className="mdd-mesh-bg" />
      <div className="mdd-grid-overlay" />

      {/* MEGAMENU: solo visible cuando estás dentro de un módulo */}
      {!isLauncher && (
        <header
          data-testid="app-megamenu"
          className="mdd-glass fixed top-0 left-0 right-0 z-40 h-16 flex items-center px-4 md:px-6"
        >
          {/* Left: logo + botón volver al launcher + módulo actual */}
          <div className="flex items-center gap-3">
            <button
              data-testid="megamenu-home-btn"
              onClick={() => onNavigate("launcher")}
              className="mdd-btn mdd-btn-ghost !p-2 !py-2"
              title="Volver al inicio"
            >
              <LayoutGrid className="w-4 h-4" strokeWidth={1.75} />
            </button>
            <button
              onClick={() => onNavigate("launcher")}
              className="flex items-center gap-2.5 hover:opacity-90 transition-opacity"
              data-testid="megamenu-brand"
            >
              <div className="w-9 h-9 rounded-lg overflow-hidden ring-1 ring-white/10 bg-mdd-elevated">
                <img src={ASSETS.logo} alt="Turismo MDD" className="w-full h-full object-contain" />
              </div>
              <div className="hidden md:block leading-tight">
                <div className="font-display text-sm font-semibold text-mdd-text tracking-tight">Turismo MDD</div>
                <div className="text-[10px] font-mono uppercase text-mdd-muted tracking-wider">Command Center</div>
              </div>
            </button>

            {/* Divider + selector rápido de módulo */}
            <div className="hidden md:block w-px h-8 bg-white/5 mx-2" />
            <div className="relative" ref={switcherRef}>
              <button
                data-testid="megamenu-switcher-btn"
                onClick={() => setSwitcherOpen((v) => !v)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5 transition-colors"
              >
                {currentModule?.icon && (
                  <currentModule.icon className="w-4 h-4 text-mdd-lime" strokeWidth={1.75} />
                )}
                <span className="text-sm font-medium">{currentModule?.label || "Módulo"}</span>
                <ChevronDown className={`w-3.5 h-3.5 text-mdd-muted transition-transform ${switcherOpen ? "rotate-180" : ""}`} />
              </button>
              {switcherOpen && (
                <div
                  data-testid="megamenu-switcher-dropdown"
                  className="absolute top-full left-0 mt-2 w-[min(560px,80vw)] mdd-card-elevated p-2 z-50 shadow-2xl shadow-black/60"
                >
                  <div className="text-[10px] font-mono uppercase text-mdd-muted px-3 pt-1 pb-2 tracking-wider">Cambiar módulo</div>
                  <div className="grid grid-cols-3 gap-1">
                    {MODULES.map((m) => (
                      <button
                        key={m.id}
                        data-testid={`switcher-item-${m.id}`}
                        onClick={() => { onNavigate(m.id); setSwitcherOpen(false); }}
                        className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left text-sm transition-all
                          ${active === m.id ? "bg-mdd-neon/15 text-mdd-lime border border-mdd-neon/25" : "hover:bg-white/5 border border-transparent"}`}
                      >
                        <m.icon className="w-4 h-4" strokeWidth={1.75} />
                        <span className="text-xs">{m.label}</span>
                        {m.badge && (
                          <span className="ml-auto text-[9px] font-mono px-1.5 py-0.5 rounded bg-mdd-danger/20 text-mdd-danger">{m.badge}</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Center: search */}
          <div className="flex-1 max-w-md mx-auto px-4 hidden md:block">
            <div className="relative">
              <Search className="w-4 h-4 text-mdd-muted absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="megamenu-search"
                type="text"
                placeholder="Buscar carreras, socios, conductores..."
                className="mdd-input !pl-9 !py-2 text-xs"
              />
            </div>
          </div>

          {/* Right: notif + user */}
          <div className="ml-auto flex items-center gap-2">
            <ThemeToggle compact />
            <NotificationsDropdown />
            <div className="relative" ref={userMenuRef}>
              <button
                data-testid="user-menu-btn"
                onClick={() => setUserMenuOpen((v) => !v)}
                className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full hover:bg-white/5 transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-mdd-neon to-mdd-indigo flex items-center justify-center text-xs font-bold text-white ring-1 ring-white/20">
                  {(user?.name || user?.email || "U").slice(0, 2).toUpperCase()}
                </div>
                <ChevronDown className="w-3.5 h-3.5 text-mdd-muted" />
              </button>
              {userMenuOpen && (
                <div
                  data-testid="user-menu-dropdown"
                  className="absolute top-full right-0 mt-2 w-56 mdd-card-elevated py-1 z-50 shadow-2xl shadow-black/60"
                >
                  <div className="px-4 py-3 border-b border-white/5">
                    <div className="text-sm font-semibold text-mdd-text">{user?.name || "Admin"}</div>
                    <div className="text-xs text-mdd-muted font-mono">{user?.email}</div>
                  </div>
                  <button
                    data-testid="user-menu-account"
                    onClick={() => { setShowMyAccount(true); setUserMenuOpen(false); }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-white/5 transition-colors"
                  >
                    <UserIcon className="w-4 h-4 text-mdd-muted" strokeWidth={1.75} />
                    Mi cuenta
                  </button>
                  <button
                    data-testid="user-menu-logout"
                    onClick={onLogout}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-mdd-danger hover:bg-mdd-danger/10 transition-colors"
                  >
                    <LogOut className="w-4 h-4" strokeWidth={1.75} />
                    Cerrar sesión
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>
      )}

      {/* Contenido */}
      <main
        data-testid="app-main"
        className={isLauncher ? "" : "pt-16 min-h-screen"}
      >
        <div className={isLauncher ? "" : "max-w-[1680px] mx-auto px-4 md:px-6 py-6"}>
          {children}
        </div>
      </main>

      {showMyAccount && (
        <MyAccountModal user={user} onClose={() => setShowMyAccount(false)} />
      )}
    </div>
  );
}
